/**
 * QuizMedia Component
 * Orchestrates rendering of quiz media (images, videos, or Desmos graphs)
 *
 * Rendering priority:
 * 1. Desmos graph (if desmos prop is provided)
 * 2. Image/Video (via QuizImage)
 */

import { cn } from '../../../lib/utils'
import type { QuizMediaObject } from '../../../types/legacyQuiz'
import { QuizImage } from './quiz-image'
import { DesmosGraph } from './desmos-graph'

interface QuizMediaProps {
  /** Main image/video object */
  image?: QuizMediaObject | null
  /** Mobile-specific image (used on screens <=768px, skipped if image is video) */
  imageMobile?: QuizMediaObject | null
  /** Desmos graph state (JSON string) - takes priority over image when provided */
  desmos?: string | null
  /** Variables for Desmos expression substitution (e.g., { _a: 5, _b: 3 }) */
  vars?: Record<string, unknown>
  /** Additional CSS classes */
  className?: string
  /** Alt text for image */
  alt?: string
}

/**
 * QuizMedia component - unified media renderer for quizzes
 *
 * Use this component when you need to render quiz media that could be
 * either an image/video or a Desmos graph. It automatically selects
 * the appropriate renderer based on the provided props.
 *
 * @example
 * // With quiz data object
 * <QuizMedia
 *   image={quiz.image}
 *   imageMobile={quiz.imageMobile}
 *   desmos={quiz.desmos}
 *   vars={quiz.vars}
 * />
 *
 * @example
 * // Image only
 * <QuizMedia image={mediaObject} />
 *
 * @example
 * // Desmos only
 * <QuizMedia desmos={desmosStateJson} vars={{ _a: 5 }} />
 */
export function QuizMedia({
  image,
  imageMobile,
  desmos,
  vars,
  className,
  alt,
}: QuizMediaProps) {
  // Priority 1: Desmos graph
  if (desmos) {
    return (
      <div data-quiz-media className={cn('flex justify-center my-4', className)}>
        <DesmosGraph state={desmos} vars={vars} className="rounded-xl" />
      </div>
    )
  }

  // Priority 2: Image/Video
  if (image) {
    return (
      <div data-quiz-media className={className}>
        <QuizImage
          image={image}
          imageMobile={imageMobile}
          alt={alt}
        />
      </div>
    )
  }

  // Nothing to render
  return null
}
